import { useCallback, useState } from "react";

import {
  ObservabilityService,
  type AuditLogFilters,
  type AuditStatus,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination } from "@/components/shared/pagination";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

const STATUS_TONE: Record<AuditStatus, BadgeTone> = {
  success: "success",
  blocked: "warning",
  error: "danger",
};

// Audit statuses map to translation keys so the status badge
// re-renders on a locale switch.
const STATUS_KEY: Record<AuditStatus, TranslationKey> = {
  success: "observability.auditStatus.success",
  blocked: "observability.auditStatus.blocked",
  error: "observability.auditStatus.error",
};

interface FilterState {
  log_status: AuditStatus | "";
  action: string;
  pii_detected: "" | "true" | "false";
}

const EMPTY_FILTERS: FilterState = {
  log_status: "",
  action: "",
  pii_detected: "",
};

function selectClasses(active: boolean) {
  return cn(
    "h-9 rounded-md border bg-white px-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ocean-400",
    active ? "border-ocean-400" : "border-ocean-100"
  );
}

export function AuditTab() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  const fetcher = useCallback(() => {
    const query: AuditLogFilters = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (filters.log_status) query.log_status = filters.log_status;
    if (filters.action.trim()) query.action = filters.action.trim();
    if (filters.pii_detected) {
      query.pii_detected = filters.pii_detected === "true";
    }
    return ObservabilityService.getAuditLogs(query);
  }, [filters, page]);

  const { data, error, isLoading } = useApi(fetcher, {
    deps: [filters, page],
  });

  const items = data?.items ?? [];
  const hasNext = items.length === PAGE_SIZE;
  const hasActiveFilters =
    filters.log_status !== "" ||
    filters.action.trim() !== "" ||
    filters.pii_detected !== "";

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-ocean-100 bg-ocean-50/30 p-2">
        <select
          aria-label={t("observability.audit.filterStatusAria")}
          value={filters.log_status}
          onChange={(e) =>
            updateFilter("log_status", e.target.value as FilterState["log_status"])
          }
          className={selectClasses(filters.log_status !== "")}
        >
          <option value="">{t("observability.audit.filterStatusAll")}</option>
          <option value="success">
            {t("observability.auditStatus.success")}
          </option>
          <option value="blocked">
            {t("observability.auditStatus.blocked")}
          </option>
          <option value="error">
            {t("observability.auditStatus.error")}
          </option>
        </select>
        <select
          aria-label={t("observability.audit.filterPiiAria")}
          value={filters.pii_detected}
          onChange={(e) =>
            updateFilter(
              "pii_detected",
              e.target.value as FilterState["pii_detected"]
            )
          }
          className={selectClasses(filters.pii_detected !== "")}
        >
          <option value="">{t("observability.audit.filterPiiAll")}</option>
          <option value="true">
            {t("observability.audit.filterPiiDetected")}
          </option>
          <option value="false">
            {t("observability.audit.filterPiiNone")}
          </option>
        </select>
        <input
          type="text"
          aria-label={t("observability.audit.filterActionAria")}
          placeholder={t("observability.audit.filterActionPlaceholder")}
          value={filters.action}
          onChange={(e) => updateFilter("action", e.target.value)}
          className={cn(
            "h-9 flex-1 min-w-[180px] rounded-md border bg-white px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ocean-400",
            filters.action.trim() ? "border-ocean-400" : "border-ocean-100"
          )}
        />
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setPage(0);
            }}
            className="rounded-md px-3 py-1 text-xs font-medium text-ocean-600 transition-colors hover:bg-ocean-100"
          >
            {t("observability.audit.reset")}
          </button>
        )}
      </div>

      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("observability.audit.colTime")}</TableHead>
              <TableHead>{t("observability.audit.colAction")}</TableHead>
              <TableHead>{t("observability.audit.colStatus")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("observability.audit.colPath")}
              </TableHead>
              <TableHead className="hidden desktop:table-cell">
                {t("observability.audit.colLatency")}
              </TableHead>
              <TableHead className="hidden desktop:table-cell">
                {t("observability.audit.colPii")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && items.length === 0 && (
              <TableEmpty
                colSpan={6}
                message={
                  hasActiveFilters
                    ? t("observability.audit.emptyFiltered")
                    : t("observability.audit.empty")
                }
              />
            )}
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-neutral-600">
                  {item.created_at ? formatDateID(item.created_at) : "—"}
                </TableCell>
                <TableCell className="font-medium text-ocean-900">
                  {item.action}
                </TableCell>
                <TableCell>
                  <Badge tone={STATUS_TONE[item.status]}>
                    {t(STATUS_KEY[item.status])}
                  </Badge>
                </TableCell>
                <TableCell className="hidden font-mono text-xs text-neutral-600 tablet:table-cell">
                  {item.method && item.path
                    ? `${item.method} ${item.path}`
                    : item.path ?? "—"}
                </TableCell>
                <TableCell className="hidden text-neutral-600 desktop:table-cell">
                  {item.latency_ms !== null && item.latency_ms !== undefined
                    ? t("observability.audit.latencyValue", {
                        ms: item.latency_ms,
                      })
                    : "—"}
                </TableCell>
                <TableCell className="hidden desktop:table-cell">
                  {item.pii_detected ? (
                    <Badge tone="warning">
                      {item.pii_types?.join(", ") ??
                        t("observability.audit.piiPresent")}
                    </Badge>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(items.length > 0 || page > 0) && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          hasNext={hasNext}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}
    </div>
  );
}
